import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Heart, MapPin, Package, Search, ShieldAlert, Star, Timer } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";

export default function Suppliers() {
    const { user } = useAuth();
    const [suppliers, setSuppliers] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [q, setQ] = useState("");
    const [city, setCity] = useState("all");
    const [minTrust, setMinTrust] = useState(0);
    const [scores, setScores] = useState({});
    const isBuyer = user?.role === "buyer";

    const load = () => {
        api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => setSuppliers([]));
        if (isBuyer) api.get("/buyers/favorites").then((r) => setFavorites(r.data.map((f) => f.supplier_id))).catch(() => {});
        api.get("/complaints/scores").then((r) => {
            const m = {};
            (r.data || []).forEach((x) => { if (x.target_type === "supplier") m[x.target_id] = x; });
            setScores(m);
        }).catch(() => {});
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const cities = [...new Set((suppliers || []).map((s) => s.city))];

    if (user?.role === "supplier") {
        return (
            <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="suppliers-restricted">
                <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
                <h1 className="mt-4 font-display text-xl font-bold">Bu sayfa yalnızca mağazalar içindir</h1>
                <p className="mt-2 text-sm text-muted-foreground">Tedarikçi hesabınızla diğer tedarikçileri görüntüleyemezsiniz.</p>
            </div>
        );
    }

    const toggleFav = async (e, s) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const r = await api.post(`/suppliers/${s.supplier_id}/favorite`);
            setFavorites((f) => (r.data.is_favorite ? [...f, s.supplier_id] : f.filter((x) => x !== s.supplier_id)));
            toast.success(r.data.is_favorite ? `${s.name} favorilere eklendi` : `${s.name} favorilerden çıkarıldı`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "İşlem başarısız");
        }
    };

    const filtered = (suppliers || [])
        .filter((s) => (!q || s.name.toLowerCase().includes(q.toLowerCase()) || s.city.toLowerCase().includes(q.toLowerCase()))
            && (city === "all" || s.city === city)
            && s.trust_score >= minTrust)
        .sort((a, b) => a.name.localeCompare(b.name, "tr"));
    const favList = filtered.filter((s) => favorites.includes(s.supplier_id));
    const restList = filtered.filter((s) => !favorites.includes(s.supplier_id));

    const Card = ({ s, i }) => (
        <div key={s.supplier_id} className="group relative animate-fade-up rounded-2xl border bg-card p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-terra-500/40 hover:shadow-md" style={{ animationDelay: `${Math.min(i, 8) * 0.05}s` }} data-testid={`supplier-card-${s.supplier_id}`}>
            {isBuyer && (
                <button onClick={(e) => toggleFav(e, s)} className="absolute right-4 top-4 z-10" data-testid={`fav-toggle-${s.supplier_id}`} title="Favori tedarikçi">
                    <Heart className={`h-5 w-5 transition-colors ${favorites.includes(s.supplier_id) ? "fill-red-500 text-red-500" : "text-stone-300 hover:text-red-400"}`} />
                </button>
            )}
            <Link to={`/panel/vitrin/${s.supplier_id}`}>
                <div className="flex items-start gap-3">
                    {s.logo ? (
                        <img src={s.logo} alt={s.name} className="h-12 w-12 rounded-xl object-cover" />
                    ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-terra-600 to-saffron-400 font-display text-lg font-extrabold text-white">
                            {s.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                    )}
                    <div>
                        <h3 className="font-display text-lg font-bold group-hover:text-primary">{s.name}</h3>
                        <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {s.city}</p>
                        {scores[s.supplier_id] && (
                            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid={`buyer-rating-${s.supplier_id}`}>
                                <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {scores[s.supplier_id].avg_rating.toFixed(1)} <span className="font-normal text-muted-foreground">({scores[s.supplier_id].total} alıcı puanı)</span>
                            </p>
                        )}
                    </div>
                    {s.verified && <Badge className="ml-auto mr-7 border-0 bg-blue-100 text-blue-800"><BadgeCheck className="mr-1 h-3 w-3" /> Doğrulanmış</Badge>}
                </div>
                <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{s.description}</p>
                <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-4 text-center">
                    <div><p className="flex items-center justify-center gap-1 font-display text-lg font-extrabold text-sage-600"><Star className="h-3.5 w-3.5 fill-saffron-400 text-saffron-400" />{s.trust_score}</p><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Güven</p></div>
                    <div><p className="flex items-center justify-center gap-1 font-display text-lg font-extrabold"><Timer className="h-3.5 w-3.5 text-muted-foreground" />%{s.on_time_rate}</p><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Zamanında</p></div>
                    <div><p className="flex items-center justify-center gap-1 font-display text-lg font-extrabold"><Package className="h-3.5 w-3.5 text-muted-foreground" />{s.product_count}</p><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Ürün</p></div>
                </div>
            </Link>
        </div>
    );

    return (
        <div className="space-y-6" data-testid="suppliers-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Tedarikçiler</h1>
                <p className="mt-1 text-sm text-muted-foreground">Alfabetik listede inceleyin, favorilerinizi işaretleyin</p>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="supplier-filters">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tedarikçi ara..." className="h-9 w-56 pl-8 text-sm" data-testid="supplier-search" />
                </div>
                <button onClick={() => setCity("all")} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${city === "all" ? "bg-primary text-primary-foreground border-primary" : ""}`} data-testid="city-filter-all">Tüm Şehirler</button>
                {cities.map((c) => (
                    <button key={c} onClick={() => setCity(c)} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${city === c ? "bg-primary text-primary-foreground border-primary" : ""}`} data-testid={`city-filter-${c}`}>{c}</button>
                ))}
                <select value={minTrust} onChange={(e) => setMinTrust(Number(e.target.value))} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="trust-filter">
                    <option value={0}>Güven: Tümü</option>
                    <option value={90}>90+</option>
                    <option value={95}>95+</option>
                </select>
            </div>

            {!suppliers ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <>
                    {favList.length > 0 && (
                        <div data-testid="favorites-section">
                            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><Heart className="h-4 w-4 fill-red-500 text-red-500" /> Favori Tedarikçilerim</h2>
                            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{favList.map((s, i) => <Card key={s.supplier_id} s={s} i={i} />)}</div>
                        </div>
                    )}
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="suppliers-grid">
                        {restList.map((s, i) => <Card key={s.supplier_id} s={s} i={i} />)}
                    </div>
                    {!filtered.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground">Filtreye uyan tedarikçi yok.</p>}
                </>
            )}
        </div>
    );
}
